// ═══════════════════════════════════════════════════════════════════
//  socle/route.js
//  L'URL comme état de navigation — sans routeur, sans dépendance
// ═══════════════════════════════════════════════════════════════════

import { NAV_ITEMS } from "../layout/navigation";

// ⚠ POURQUOI PAS react-router-dom.
//
// La navigation de ce site passe déjà par UN SEUL point : `naviguer(module,
// cible)` dans App.js. Il n'y a rien à router — il y a une URL à tenir à jour.
// Installer un routeur imposerait de réécrire les dix-sept branches
// d'affichage et le passage de `cible`, pour un résultat identique.
//
// Ce fichier fait les trois choses qui manquaient, en s'appuyant sur
// l'History API du navigateur :
//   - lire l'URL au démarrage (F5 revient là où on était) ;
//   - la pousser à chaque navigation (le lien devient partageable) ;
//   - écouter le retour arrière (le bouton Précédent redevient utile).
//
// ⚠ LE PIÈGE CÔTÉ SERVEUR EST DÉJÀ FERMÉ. Un accès direct à
// `/dashboard` ne trouve aucun fichier de ce nom : sans réécriture, IIS
// rendrait un 404. La règle « React SPA » du `client\web.config` renvoie déjà
// `index.html` pour toute URL qui n'est pas un fichier. Rien à changer là —
// mais si quelqu'un touche ce web.config un jour, c'est cette règle qui fait
// vivre les URL de cette page.

// La liste des modules valides vient de NAV_ITEMS, jamais d'une copie.
// Un module ajouté au menu obtient donc son URL sans qu'on y pense — six l'ont
// été cette semaine.
const MODULES = new Set(NAV_ITEMS.map((it) => it.id));

/** Le module par défaut, quand l'URL ne dit rien ou dit n'importe quoi. */
export const MODULE_DEFAUT = "dashboard";

/**
 * Le module demandé par l'URL courante.
 *
 * ⚠ On VALIDE contre NAV_ITEMS au lieu de faire confiance à l'URL. Sans ça,
 * `/nimportequoi` laisserait l'application sur un écran vide : aucune des
 * dix-sept branches d'affichage ne correspondrait, et l'utilisateur verrait
 * une page blanche sans savoir pourquoi. On retombe sur le tableau de bord.
 */
export function moduleDepuisUrl() {
  const seg = premierSegment();
  return MODULES.has(seg) ? seg : MODULE_DEFAUT;
}

/**
 * La cible éventuelle : le second segment. `/tickets/42` ouvre les tickets
 * SUR le ticket 42.
 *
 * Rendue en TEXTE, toujours. Les modules qui la reçoivent la comparent à des
 * identifiants ; les convertir en nombre ici ferait diverger `42` et `"42"`
 * selon qu'on arrive par un clic ou par une URL — le genre d'écart qui ne se
 * voit qu'un jour sur dix.
 */
export function cibleDepuisUrl() {
  const parts = segments();
  return parts.length >= 2 && parts[1] ? decodeURIComponent(parts[1]) : null;
}

/**
 * Écrit l'URL correspondant à un module et une cible.
 *
 * @param remplacer  true au premier rendu : on CORRIGE l'URL sans empiler une
 *                   entrée d'historique. Sans ça, le premier Précédent ne
 *                   ferait rien du tout — il reviendrait sur la même page.
 */
export function ecrireUrl(module, cible, remplacer) {
  const url = chemin(module, cible);
  if (url === window.location.pathname + window.location.search) return;
  try {
    window.history[remplacer ? "replaceState" : "pushState"]({ module, cible: texte(cible) }, "", url);
  } catch (e) {
    // Un navigateur qui refuse l'History API ne doit pas casser la navigation :
    // l'application continue de fonctionner, elle perd seulement ses URL.
  }
}

export function chemin(module, cible) {
  const c = texte(cible);
  return "/" + encodeURIComponent(module) + (c ? "/" + encodeURIComponent(c) : "");
}

/**
 * ⚠ Une cible n'entre dans l'URL que si elle est un TEXTE ou un NOMBRE.
 *
 * `naviguer()` accepte n'importe quoi comme cible, et certains modules lui
 * passent un objet. `String({})` donnerait « [object Object] » dans la barre
 * d'adresse, et le lien partagé n'ouvrirait rien. Dans ce cas on garde l'URL
 * du module seul : elle est juste, simplement moins précise.
 */
function texte(cible) {
  if (cible === null || cible === undefined) return null;
  const t = typeof cible;
  if (t !== "string" && t !== "number") return null;
  const s = String(cible).trim();
  return s === "" ? null : s;
}

function segments() {
  return window.location.pathname.split("/").filter(Boolean);
}

function premierSegment() {
  const parts = segments();
  return parts.length ? decodeURIComponent(parts[0]) : "";
}
